import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Users, Building2, Target, Banknote, FileWarning, ShieldAlert, Lock,
  Network, Sparkles, GraduationCap, FolderLock, TerminalSquare, TrendingUp, ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import GrowthDashboard from "@/components/GrowthDashboard";
import SetupChecklist from "@/components/setup/SetupChecklist";

const fmtQar = (v) => `QAR ${Number(v).toLocaleString()}`;

const KpiCard = ({ icon: Icon, label, value, sub, testId, accent }) => (
  <div className="bg-white border border-border p-5" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
      <Icon className={`h-4 w-4 ${accent ? "text-teal-600" : "text-slate-400"}`} strokeWidth={2} />
    </div>
    <p className="text-3xl font-bold text-[var(--brand-primary)] mt-3 tracking-tight">{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>}
  </div>
);

const ChartCard = ({ title, children, testId }) => (
  <Card className="rounded-none border-border shadow-sm" data-testid={testId}>
    <CardHeader className="pb-2 border-b border-border">
      <CardTitle className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--brand-primary)]">{title}</CardTitle>
    </CardHeader>
    <CardContent className="pt-5">{children}</CardContent>
  </Card>
);

const MODULES = [
  { key: "nav.employees", testId: "quick-link-employees", path: "/employees", icon: Users },
  { key: "nav.orgIntelligence", testId: "quick-link-org-intelligence", path: "/org", icon: Network },
  { key: "dashboard.quickSkills", testId: "quick-link-skills", path: "/skills", icon: Sparkles },
  { key: "dashboard.quickLearning", testId: "quick-link-learning", path: "/learning", icon: GraduationCap },
  { key: "dashboard.quickDocuments", testId: "quick-link-documents", path: "/documents", icon: FolderLock },
  { key: "nav.payroll", testId: "quick-link-payroll", path: "/payroll", icon: Banknote, roles: ["admin", "hr_manager"] },
  { key: "nav.performance", testId: "quick-link-performance", path: "/performance", icon: TrendingUp },
  { key: "nav.openQuery", testId: "quick-link-open-query", path: "/query", icon: TerminalSquare, roles: ["admin", "hr_manager", "dept_head"] },
  { key: "dashboard.quickSecurity", testId: "quick-link-security", path: "/security", icon: ShieldCheck, roles: ["admin", "hr_manager"] },
];

const QuickLinks = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const visible = MODULES.filter((m) => !m.roles || m.roles.includes(user.role));
  return (
    <div className="flex flex-wrap gap-2" data-testid="dashboard-quick-links">
      {visible.map(({ key, testId, path, icon: Icon }) => (
        <Link key={path} to={path} data-testid={testId}
          className="inline-flex items-center gap-2 bg-white border border-border px-3 py-2 text-[12px] text-slate-600 hover:border-teal-600 hover:text-teal-800 transition-colors">
          <Icon className="h-3.5 w-3.5" /> {t(key)}
        </Link>
      ))}
    </div>
  );
};

function ExecutiveDashboard({ stats }) {
  const { t } = useTranslation();
  const showPayroll = stats.payroll_monthly_cost != null;
  return (
    <div className="space-y-6 fade-in" data-testid="executive-dashboard">
      <SetupChecklist />
      <QuickLinks />
      {stats.scope !== "company" && (
        <Badge variant="outline" className="rounded-none border-teal-600/50 text-teal-800 text-[11px] font-mono">
          {t("dashboard.scopeDept", { scope: stats.scope })}
        </Badge>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard icon={Users} label={t("dashboard.headcount")} value={stats.headcount} testId="kpi-headcount" accent />
        <KpiCard icon={Building2} label={t("dashboard.departments")} value={stats.departments} testId="kpi-departments" />
        <KpiCard icon={Target} label={t("dashboard.skillCoverage")} value={`${stats.avg_skill_coverage}%`} sub={t("dashboard.avgProficiency")} testId="kpi-skill-coverage" accent />
        {showPayroll ? (
          <KpiCard icon={Banknote} label={t("dashboard.payrollMonth")} value={fmtQar(stats.payroll_monthly_cost)} testId="kpi-payroll" />
        ) : (
          <div className="bg-white border border-border p-5 flex flex-col justify-center items-center text-muted-foreground" data-testid="kpi-payroll-restricted">
            <Lock className="h-4 w-4 mb-1.5" />
            <p className="text-[11px] uppercase tracking-wider">{t("dashboard.payrollRestricted")}</p>
          </div>
        )}
        <KpiCard icon={FileWarning} label={t("dashboard.expiring90")} value={stats.docs_expiring_90d} sub={t("dashboard.documents")} testId="kpi-expiring-docs" />
        <KpiCard icon={ShieldAlert} label={t("dashboard.complianceRisks")} value={stats.open_compliance_risks} sub={t("dashboard.expiredOr30")} testId="kpi-compliance-risks" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ChartCard title={t("dashboard.headcountByDept")} testId="chart-headcount">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.headcount_by_department} margin={{ left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="department" tick={{ fontSize: 11, fill: "#64748B" }} interval={0} angle={-14} height={44} textAnchor="end" />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 0, fontSize: 12 }} />
              <Bar dataKey="count" fill="var(--brand-primary)" maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {showPayroll && stats.salary_by_department && (
          <ChartCard title={t("dashboard.salaryCostByDept")} testId="chart-salary">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={stats.salary_by_department} margin={{ left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="department" tick={{ fontSize: 11, fill: "#64748B" }} interval={0} angle={-14} height={44} textAnchor="end" />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v) => fmtQar(v)} cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 0, fontSize: 12 }} />
                <Bar dataKey="cost" fill="var(--brand-accent)" maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        <ChartCard title={t("dashboard.expiryTimeline")} testId="chart-expiry">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stats.expiry_timeline} margin={{ left: -22 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 0, fontSize: 12 }} />
              <Bar dataKey="count" fill="#94A3B8" maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("dashboard.skillsByCategory")} testId="chart-skills">
          <div className="space-y-3">
            {stats.skills_distribution.map((s) => (
              <div key={s.category} className="flex items-center gap-3">
                <span className="w-28 text-[12px] text-slate-600 shrink-0">{s.category}</span>
                <div className="flex-1 h-2 bg-slate-100">
                  <div className="h-2 bg-teal-600" style={{ width: `${(s.avg_proficiency / 5) * 100}%` }} />
                </div>
                <span className="text-[11px] font-mono text-slate-500 w-20 text-right">
                  {s.avg_proficiency}/5 · {s.count}
                </span>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user.role !== "employee") {
      api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    }
  }, [user.role]);

  if (user.role === "employee") return <GrowthDashboard employeeId={user.employee_id} />;
  if (!stats) {
    return (
      <div className="grid grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-28 rounded-none" />)}
      </div>
    );
  }
  return <ExecutiveDashboard stats={stats} />;
}
